/**
 * Generates an array of n visually distinct hex/HSL color strings.
 * Evenly distributes hues around the color wheel.
 *
 * @param {number} count - Total number of hardware colors needed.
 * @param {number} [saturation=70] - Color saturation percentage.
 * @param {number} [lightness=45] - Color lightness percentage.
 * @returns {Array<string>} Array of HSL color strings.
 */
export function generateDevicePalette(count, saturation = 70, lightness = 45) {
  if (count <= 0) return [];
  
  const colors = [];
  // Use a golden-ratio or even step interval to separate adjacent colors cleanly
  const step = 360 / count;

  for (let i = 0; i < count; i++) {
    // Starting hue offset (e.g. 140deg for green/teal tones)
    const hue = Math.round((140 + i * step) % 360);
    colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`);
  }

  return colors;
}


export function getLighterColor(colorStr, percent = 55) {
  if (!colorStr) return colorStr;

  if (colorStr.startsWith('hsl')) {
    const match = colorStr.match(/hsl\((\d+),\s*([\d.]+)%,\s*([\d.]+)%\)/);
    if (match) {
      const [, h, s, l] = match;
      const newL = Math.min(100, Number(l) + percent);
      return `hsl(${h}, ${s}%, ${newL}%)`;
    }
  }

  // Handle Hex colors (#rgb or #rrggbb)
  let hex = colorStr.trim();
  if (hex.startsWith('#')) {
    hex = hex.slice(1);
  }

  // Expand 3-digit hex to 6-digit
  if (hex.length === 3) {
    hex = hex.split('').map((char) => char + char).join('');
  }

  if (/^[0-9A-Fa-f]{6}$/.test(hex)) {
    let r = parseInt(hex.slice(0, 2), 16);
    let g = parseInt(hex.slice(2, 4), 16);
    let b = parseInt(hex.slice(4, 6), 16);

    // Lighten channels toward 255 (white) based on percentage
    const factor = percent / 100;
    r = Math.round(r + (255 - r) * factor);
    g = Math.round(g + (255 - g) * factor);
    b = Math.round(b + (255 - b) * factor);

    // Clip values so they do not exceed FF (255)
    r = Math.max(0, Math.min(255, r));
    g = Math.max(0, Math.min(255, g));
    b = Math.max(0, Math.min(255, b));

    const toHex = (n) => {
      const hStr = n.toString(16).toUpperCase();
      return hStr.length === 1 ? '0' + hStr : hStr;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  // Fallback if RGB or unrecognized format
  return colorStr;
}
